import { Box, Typography } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { EAttributeType, EAttributeUnit } from '../../@types/AttributeTypes';
import { EUnitType } from '../../@types/Enums';
import AttributeModel from '../../models/standard/AttributeModel';
import { usePlan } from '../../provider/PlanProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import {
  attributeUnitDisplayMap,
  convertAttributeValue,
  getCorrespondingUnit,
  getLocalizedAttributeLabel,
  IMPERIAL_TO_METRIC,
  METRIC_SCALE_FACTORS
} from '../../utils/attributes/attributeUtils';
import { getSelectionDisplayText } from '../../utils/attributes/selectionAttributeUtils';

interface AttributeListFixedProps {
  attributes: AttributeModel[];
  originRegion?: string;
}

const AttributeListFixed: React.FC<AttributeListFixedProps> = ({ attributes, originRegion }) => {
  const intl = useIntl();
  const { locale } = intl;
  const { unitType } = useUnitContext();
  const { planModel } = usePlan();

  const regionKey = originRegion ? originRegion.toUpperCase() : (planModel?.region ? String(planModel.region).toUpperCase() : 'DE');
  const defaultsRegionKey = regionKey === 'US' ? 'US' : 'DE';
  
  const isImperialDisplay = unitType === EUnitType.IMPERIAL;

  const validAttributes = attributes?.filter(a => a && a.key && a.key.trim() !== '') || [];

  if (validAttributes.length === 0) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={1} mt={2}>
      <Typography variant="subtitle2" sx={{ color: '#4a4a4a', textTransform: 'uppercase', fontWeight: 'bold' }}>
        {intl.formatMessage({ id: 'AttributeList.attributes', defaultMessage: 'Attributes' })}
      </Typography>
      
      {validAttributes.map((attr) => {
        const key = getLocalizedAttributeLabel(attr, locale);
        
        const defaultObj = attr.defaults?.[defaultsRegionKey] || attr.defaults?.GLOBAL;
        let effectiveVal = attr.value;

        if (effectiveVal === undefined || effectiveVal === null || effectiveVal === '') {
           if (attr.type === EAttributeType.BOOL) {
               effectiveVal = 'true';
           } else if (defaultObj?.value !== undefined && defaultObj?.value !== null) {
               effectiveVal = String(defaultObj.value);
           }
        }
        
        let valStr = String(effectiveVal ?? '');
        let unitLabel = '';

        if (attr.type === EAttributeType.NUMBER) {
           let storedUnit = attr.unit;
           if (!storedUnit) {
            const validationObj = attr.validation?.[defaultsRegionKey] || attr.validation?.GLOBAL || {};
            const valUnit = (validationObj as any).unit;
            storedUnit = valUnit || defaultObj?.unit;
          }

           if (storedUnit) {
             const sourceUnit = storedUnit as EAttributeUnit;
             const targetUnit = getCorrespondingUnit(sourceUnit, isImperialDisplay);

             unitLabel = attributeUnitDisplayMap[targetUnit] || targetUnit;

             if (targetUnit !== sourceUnit) {
                const num = parseFloat(valStr);
                if (!isNaN(num)) {
                   // 1. Basic Conversion (Result is Imperial OR Metric Base/Meters)
                   let converted = convertAttributeValue(num, sourceUnit, isImperialDisplay);

                   // 2. MAGNITUDE FIX:
                   // If we converted Imperial -> Metric, the result is in Meters (0.03378).
                   // We must divide by the target scale (e.g. 0.01 for CM) to get (3.378).
                   if (!isImperialDisplay && IMPERIAL_TO_METRIC[sourceUnit]) {
                       const scale = METRIC_SCALE_FACTORS[targetUnit] || 1;
                       converted = converted / scale;
                   }

                   valStr = String(Math.round(converted * 1000) / 1000);
                }
             }
           }
        } 
        else if (attr.type === EAttributeType.BOOL) {
          const yes = intl.formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' });
          const no = intl.formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' });
          valStr = valStr === 'true' ? yes : no;
        } else if (attr.type === EAttributeType.SELECTION) {
          valStr = getSelectionDisplayText(effectiveVal, locale);
        }

        const displayValue = unitLabel ? `${valStr} ${unitLabel}` : valStr;

        return (
          <Box key={attr.id} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: '#464646', fontWeight: 500, flex: 1 }}>
              {key}:
            </Typography>
            <Typography variant="body2" sx={{ color: '#464646', flex: 1, textAlign: 'right' }}>
              {displayValue}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AttributeListFixed;